// review-local-adapter.ts
//
// P7 §7 test 29-32: `review-local-v1` — worker (pr-gatekeeper-local +
// code-reviewer skill/agent trên candidate) ghi report vào STAGING dir
// (KHÔNG ghi vào review-state, worker không có quyền đó). Controller đọc
// report, kiểm secret-scan verdict có mặt + risk ≤ medium, RỒI TỰ MÌNH ghi
// receipt vào review-state-store.ts (P3) — không bao giờ để worker gọi
// `writeReviewReceipt` (đó là quyền tuyệt đối của controller, §9.3).
//
// Push bị chặn (không ghi receipt "clean") khi:
//  - risk > medium (low/medium mở, high/critical chặn)
//  - thiếu secret-scan verdict trong report worker
import { type ReviewReceipt, writeReviewReceipt } from "./review-state-store.js";

export type ReviewRisk = "low" | "medium" | "high" | "critical";

const RISK_RANK: Record<ReviewRisk, number> = { low: 0, medium: 1, high: 2, critical: 3 };
const MEDIUM_RANK = RISK_RANK.medium;

/** Report shape a worker writes to staging — untrusted input, validated below. */
export interface WorkerReviewReport {
  risk?: ReviewRisk | string;
  secret_scan?: { verdict: string; findings: number };
  evidence_hashes?: string[];
}

export interface ReviewLocalControllerInput {
  report: WorkerReviewReport;
  stagingReportPath: string;
  controlRoot: string;
  missionId: string;
  repoPath: string;
  candidateSha: string;
  baseSha: string;
  policyDigest: string;
  adapterDigest: string;
  eventSeq: number;
  now?: () => Date;
}

export type ReviewLocalControllerResult =
  | { status: "receipt_written"; receiptPath: string; receipt: ReviewReceipt }
  | { status: "blocked"; reason: string };

function isValidRisk(value: unknown): value is ReviewRisk {
  return typeof value === "string" && value in RISK_RANK;
}

/**
 * Controller-only step (test 30: NOT called by worker). Reads the worker's
 * staging report, enforces the two gate rules, and — only when both pass —
 * writes the receipt via review-state-store.ts. The worker function
 * (`writeWorkerReviewReportToStaging`) below never touches review-state.
 */
export async function controllerRecordReviewReceiptFromReport(
  input: ReviewLocalControllerInput,
): Promise<ReviewLocalControllerResult> {
  const { report } = input;

  if (report.secret_scan === undefined) {
    return {
      status: "blocked",
      reason: "Report review-local thiếu secret-scan verdict ⇒ chặn push (test 32).",
    };
  }
  if (!isValidRisk(report.risk)) {
    return {
      status: "blocked",
      reason: `Report review-local có risk không hợp lệ (nhận "${String(report.risk)}") ⇒ chặn push.`,
    };
  }
  if (RISK_RANK[report.risk] > MEDIUM_RANK) {
    return {
      status: "blocked",
      reason: `Review risk "${report.risk}" > medium ⇒ chặn push (test 31).`,
    };
  }

  const now = input.now ?? (() => new Date());
  const receipt: ReviewReceipt = {
    mission_id: input.missionId,
    repo_key: "", // overwritten below by deriveRepoKey via writeReviewReceipt's own path derivation
    repo_path: input.repoPath,
    candidate_sha: input.candidateSha,
    base_sha: input.baseSha,
    policy_digest: input.policyDigest,
    adapter_digest: input.adapterDigest,
    review_risk: report.risk,
    secret_scan: report.secret_scan,
    evidence_hashes: report.evidence_hashes ?? [],
    event_seq: input.eventSeq,
    written_at: now().toISOString(),
  };
  // repo_key must be derived from the canonical path, not trusted from
  // anywhere else — review-state-store.ts's writeReviewReceipt derives its
  // own storage path from repo_path, so we mirror the same derivation for
  // the value stored IN the receipt (receiptOpensGate compares repo_key).
  const { deriveRepoKey } = await import("./review-state-store.js");
  receipt.repo_key = deriveRepoKey(input.repoPath);

  const receiptPath = await writeReviewReceipt(input.controlRoot, receipt);
  return { status: "receipt_written", receiptPath, receipt };
}

/**
 * Worker-side step: writes ONLY to the staging dir, never to review-state.
 * This is the entire surface the worker session is allowed to touch for
 * review-local-v1 (test 29).
 */
export async function writeWorkerReviewReportToStaging(
  stagingReportPath: string,
  report: WorkerReviewReport,
): Promise<void> {
  const { writeFileDurable } = await import("./durable-file-writer.js");
  await writeFileDurable(
    stagingReportPath,
    Buffer.from(`${JSON.stringify(report, null, 2)}\n`, "utf8"),
  );
}
